<template>
  <div class="min-h-screen bg-white">
    <TrustBar />
    <div class="pt-18"></div>

    <!-- Loading State -->
    <div v-if="loading" class="min-h-screen flex items-center justify-center">
      <div class="text-center">
        <div
          class="inline-block w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"
        ></div>
        <p class="mt-4 text-gray-600">Chargement...</p>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="min-h-screen flex items-center justify-center"
    >
      <div class="text-center">
        <div class="text-7xl mb-6">⚠️</div>
        <h3 class="text-2xl font-bold text-gray-900 mb-3">{{ error }}</h3>
        <router-link
          to="/esim"
          class="inline-block px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition"
        >
          Retour aux forfaits eSIM
        </router-link>
      </div>
    </div>

    <!-- Content -->
    <template v-else>
      <!-- Breadcrumb -->
      <div class="bg-gray-50 py-3 border-b border-gray-200">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav class="flex text-sm">
            <router-link to="/" class="text-gray-500 hover:text-gray-900"
              >Accueil</router-link
            >
            <span class="mx-2 text-gray-400">/</span>
            <router-link to="/esim" class="text-gray-500 hover:text-gray-900"
              >eSIM</router-link
            >
            <span class="mx-2 text-gray-400">/</span>
            <span class="text-gray-900 font-medium">{{
              packageInfo?.name
            }}</span>
          </nav>
        </div>
      </div>

      <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div class="grid gap-8 lg:gap-12 lg:grid-cols-12 lg:mt-0">
          <!-- Left Column: Package Image -->
          <div class="lg:col-span-5 relative lg:mt-12 mt-8">
            <div
              class="flex justify-center lg:flex-col gap-4 lg:sticky lg:top-32"
            >
              <div class="relative">
                <!-- Gradient background effect -->
                <div
                  class="absolute right-1/4 top-1/2 -translate-y-1/2 h-[calc(100%+3.5rem)] w-[200vw] -z-10 rounded-full lg:h-[calc(100%+10.5rem)] lg:-top-8 lg:translate-y-0 bg-gradient-to-r from-emerald-50 via-teal-50 to-transparent/5"
                ></div>
                <div
                  class="relative px-12 h-36 md:h-48 lg:h-64 w-auto lg:w-[400px] rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-[0_0_4px_rgba(0,0,0,0.25)] flex items-center justify-center"
                >
                  <div class="text-center text-white">
                    <div class="text-5xl md:text-6xl font-bold mb-2">
                      {{ formatData(packageInfo?.volume) }}
                    </div>
                    <div class="text-lg md:text-xl opacity-90">
                      {{ packageInfo?.duration }} jours
                    </div>
                  </div>
                </div>
              </div>
              <!-- USP Badges (Desktop) -->
              <div class="hidden lg:block relative">
                <ul class="flex flex-wrap gap-2 items-center lg:items-start">
                  <li
                    class="inline-flex items-center gap-2 bg-gray-100 h-8 rounded-full px-4 w-auto"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    <span class="text-xs">Activation instantanée</span>
                  </li>
                  <li
                    class="inline-flex items-center gap-2 bg-gray-100 h-8 rounded-full px-4 w-auto"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                    <span class="text-xs">Paiement sûr et sécurisé</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Right Column: Form -->
          <div
            class="lg:col-span-7 flex flex-1 flex-col items-center lg:items-start mt-3 lg:mt-2"
          >
            <div class="w-full flex flex-1 flex-col mb-5 lg:mb-6">
              <form @submit.prevent="goToCheckout" class="flex flex-col">
                <!-- Title -->
                <div class="flex flex-row flex-wrap gap-x-2 items-center">
                  <h1 class="font-bold text-xl md:text-3xl mb-2">
                    eSIM {{ country?.countryName || '' }} -
                    {{ packageInfo?.name }}
                  </h1>
                </div>

                <!-- Country and Delivery Info -->
                <div class="flex flex-wrap gap-3 md:gap-4 mt-5 mb-6">
                  <!-- Country Selector -->
                  <div class="flex flex-1 min-w-[200px] md:flex-initial">
                    <button
                      type="button"
                      class="rounded-2xl flex items-center flex-1 justify-between border border-gray-200 bg-white px-4 py-3 hover:border-gray-300 hover:shadow-sm transition-all duration-200"
                    >
                      <div class="flex items-center gap-3">
                        <div
                          class="flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl w-11 h-11 shadow-inner"
                        >
                          <span v-if="country?.localLogoUrl">
                            <img
                              :src="country.localLogoUrl"
                              :alt="country.countryName"
                              class="w-6 h-4 object-cover rounded-sm"
                            />
                          </span>
                          <span
                            v-else
                            class="text-base font-bold text-gray-700"
                            >{{ country?.countryCode || 'BJ' }}</span
                          >
                        </div>
                        <div class="text-left">
                          <span class="text-xs text-gray-500 block"
                            >Pays de destination</span
                          >
                          <span class="text-sm font-bold text-gray-900">{{
                            country?.countryName || 'Bénin'
                          }}</span>
                        </div>
                      </div>
                    </button>
                  </div>
                  <!-- Data Allowance -->
                  <div class="flex flex-1 min-w-[200px] md:flex-initial">
                    <button
                      type="button"
                      class="rounded-2xl flex items-center flex-1 justify-between border border-gray-200 bg-white px-4 py-3 hover:border-gray-300 hover:shadow-sm transition-all duration-200"
                    >
                      <div class="flex items-center gap-3">
                        <div
                          class="flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl w-11 h-11 shadow-inner"
                        >
                          <svg
                            class="w-6 h-6 text-emerald-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
                            />
                          </svg>
                        </div>
                        <div class="text-left">
                          <span class="text-xs text-gray-500 block">Data</span>
                          <span class="text-sm font-bold text-gray-900">{{
                            formatData(packageInfo?.volume)
                          }}</span>
                        </div>
                      </div>
                    </button>
                  </div>
                  <!-- Validity -->
                  <div class="flex flex-1 min-w-[200px] md:flex-initial">
                    <button
                      type="button"
                      class="rounded-2xl flex items-center flex-1 justify-between border border-gray-200 bg-white px-4 py-3 hover:border-gray-300 hover:shadow-sm transition-all duration-200"
                    >
                      <div class="flex items-center gap-3">
                        <div
                          class="flex items-center justify-center bg-gradient-to-br from-fuchsia-50 to-pink-50 rounded-xl w-11 h-11 shadow-inner"
                        >
                          <svg
                            class="w-6 h-6 text-fuchsia-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <div class="text-left">
                          <span class="text-xs text-gray-500 block"
                            >Validité</span
                          >
                          <span class="text-sm font-bold text-gray-900"
                            >{{ packageInfo?.duration }} jours</span
                          >
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                <!-- Package Price -->
                <div class="mb-6">
                  <div
                    class="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl p-6"
                  >
                    <div class="flex items-baseline justify-between">
                      <div>
                        <span class="text-sm text-gray-600"
                          >Prix du forfait</span
                        >
                        <div class="text-3xl font-bold text-emerald-700 mt-1">
                          {{ formatPrice(packageInfo?.price) }}
                        </div>
                      </div>
                      <div class="text-right">
                        <span class="text-sm text-gray-600">Vitesse</span>
                        <div class="text-lg font-semibold text-gray-900 mt-1">
                          {{ packageInfo?.speed || '4G/5G' }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Quantity Selector -->
                <div class="mb-6">
                  <div
                    class="inline-flex items-center gap-4 bg-white border-2 border-gray-200 rounded-2xl px-4 py-3 hover:border-gray-300 transition-colors duration-200"
                  >
                    <span class="text-sm font-semibold text-gray-700"
                      >Nombre d'eSIM</span
                    >
                    <div class="flex items-center gap-2">
                      <button
                        type="button"
                        @click="decrementQuantity"
                        :disabled="quantity <= 1"
                        class="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-100"
                      >
                        <svg
                          class="w-4 h-4 text-gray-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2.5"
                            d="M20 12H4"
                          />
                        </svg>
                      </button>
                      <span
                        class="font-bold text-lg min-w-[28px] text-center text-gray-900"
                        >{{ quantity }}</span
                      >
                      <button
                        type="button"
                        @click="incrementQuantity"
                        class="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-100 hover:bg-emerald-200 active:bg-emerald-300 transition-all"
                      >
                        <svg
                          class="w-4 h-4 text-emerald-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2.5"
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Total Price Display -->
                <div class="mb-6">
                  <div
                    class="bg-gray-50 rounded-2xl p-4 flex items-center justify-between"
                  >
                    <span class="text-sm text-gray-600">Total à payer</span>
                    <span class="text-2xl font-bold text-gray-900">{{
                      formatPrice(calculateTotal())
                    }}</span>
                  </div>
                </div>

                <!-- Buy Button -->
                <div class="mb-6">
                  <button
                    type="submit"
                    class="w-full rounded-2xl flex justify-center items-center bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white min-h-14 h-14 font-bold text-base transition-all duration-200 shadow-lg shadow-emerald-200 hover:shadow-xl hover:shadow-emerald-300 active:scale-[0.98]"
                  >
                    Acheter {{ quantity }} eSIM{{ quantity > 1 ? 's' : '' }} •
                    {{ formatPrice(calculateTotal()) }}
                  </button>
                </div>

                <!-- Payment Methods -->
                <div
                  class="flex flex-col gap-4 items-center justify-centerlg:items-start mb-6"
                >
                  <div class="flex items-center gap-2">
                    <ul class="flex gap-2 p-0 m-0 list-none">
                      <li
                        class="border border-gray-200 rounded-lg p-1.5 bg-white flex items-center justify-center w-10 h-8 shadow-sm"
                      >
                        <svg class="h-4 w-auto" viewBox="0 0 36 16" fill="none">
                          <text
                            x="0"
                            y="12"
                            font-size="10"
                            font-weight="bold"
                            fill="#1A1F71"
                          >
                            VISA
                          </text>
                        </svg>
                      </li>
                      <li
                        class="border border-gray-200 rounded-lg p-1.5 bg-white flex items-center justify-center w-10 h-8 shadow-sm"
                      >
                        <svg class="h-4 w-auto" viewBox="0 0 36 16" fill="none">
                          <circle cx="12" cy="8" r="6" fill="#EB001B" />
                          <circle cx="24" cy="8" r="6" fill="#F79E1B" />
                        </svg>
                      </li>
                      <li
                        class="border border-gray-200 rounded-lg p-1.5 bg-white flex items-center justify-center w-10 h-8 shadow-sm"
                      >
                        <svg class="h-4 w-auto" viewBox="0 0 36 16" fill="none">
                          <text
                            x="0"
                            y="12"
                            font-size="8"
                            font-weight="bold"
                            fill="#003087"
                          >
                            PayPal
                          </text>
                        </svg>
                      </li>
                    </ul>
                  </div>
                </div>
              </form>

              <!-- USP Badges (Mobile) -->
              <div class="lg:hidden">
                <ul class="flex flex-wrap gap-2 items-center">
                  <li
                    class="inline-flex items-center gap-2 bg-gray-100 h-8 rounded-full px-4"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    <span class="text-xs">Activation instantanée</span>
                  </li>
                  <li
                    class="inline-flex items-center gap-2 bg-gray-100 h-8 rounded-full px-4"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                    <span class="text-xs">Paiement sûr et sécurisé</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- About Section -->
        <aside class="lg:col-span-10 lg:col-start-2 mt-8 lg:mt-10">
          <div class="grid gap-8 lg:gap-12 lg:grid-cols-12">
            <div class="lg:col-span-5">
              <section>
                <h2 class="font-bold text-xl md:text-2xl mb-4">
                  À propos de cette eSIM
                </h2>
                <div class="text-gray-600 space-y-4">
                  <p>
                    Restez connecté lors de vos voyages avec cette eSIM pour
                    {{ country?.countryName }}. Profitez de
                    {{ formatData(packageInfo?.volume) }} de data à haute
                    vitesse pendant {{ packageInfo?.duration }} jours.
                  </p>
                  <p>
                    Activation instantanée par QR code. Pas besoin de carte SIM
                    physique. Compatible avec tous les smartphones débloqués
                    eSIM.
                  </p>
                </div>
              </section>
            </div>

            <!-- FAQ Section -->
            <div class="lg:col-span-7 flex flex-col">
              <section>
                <h2 class="font-bold text-xl md:text-2xl mb-5">
                  Questions fréquemment posées
                </h2>
                <div class="space-y-3">
                  <!-- FAQ Item 1 -->
                  <div
                    class="rounded-2xl bg-white border-2 border-gray-200 overflow-hidden transition-all duration-200"
                    :class="{ 'border-emerald-200 shadow-md': openFaq === 0 }"
                  >
                    <button
                      @click="toggleFaq(0)"
                      class="w-full text-left p-5 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 class="font-semibold text-base text-gray-900 pr-4">
                        Comment activer mon eSIM ?
                      </h3>
                      <div
                        class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200"
                        :class="
                          openFaq === 0
                            ? 'bg-emerald-100 rotate-180'
                            : 'bg-gray-100'
                        "
                      >
                        <svg
                          class="w-4 h-4"
                          :class="
                            openFaq === 0 ? 'text-emerald-700' : 'text-gray-600'
                          "
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>
                    <div v-show="openFaq === 0" class="px-5 pb-5 pt-0">
                      <p class="text-gray-600 leading-relaxed">
                        Après votre achat, vous recevrez un QR code par email.
                        Scannez-le depuis les réglages de votre téléphone pour
                        activer instantanément votre eSIM.
                      </p>
                    </div>
                  </div>

                  <!-- FAQ Item 2 -->
                  <div
                    class="rounded-2xl bg-white border-2 border-gray-200 overflow-hidden transition-all duration-200"
                    :class="{ 'border-emerald-200 shadow-md': openFaq === 1 }"
                  >
                    <button
                      @click="toggleFaq(1)"
                      class="w-full text-left p-5 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 class="font-semibold text-base text-gray-900 pr-4">
                        Mon téléphone est-il compatible ?
                      </h3>
                      <div
                        class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200"
                        :class="
                          openFaq === 1
                            ? 'bg-emerald-100 rotate-180'
                            : 'bg-gray-100'
                        "
                      >
                        <svg
                          class="w-4 h-4"
                          :class="
                            openFaq === 1 ? 'text-emerald-700' : 'text-gray-600'
                          "
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>
                    <div v-show="openFaq === 1" class="px-5 pb-5 pt-0">
                      <p class="text-gray-600 leading-relaxed">
                        La plupart des smartphones récents (iPhone XS et plus,
                        Samsung Galaxy S20 et plus) sont compatibles eSIM.
                        Vérifiez dans les réglages de votre téléphone.
                      </p>
                    </div>
                  </div>

                  <!-- FAQ Item 3 -->
                  <div
                    class="rounded-2xl bg-white border-2 border-gray-200 overflow-hidden transition-all duration-200"
                    :class="{ 'border-emerald-200 shadow-md': openFaq === 2 }"
                  >
                    <button
                      @click="toggleFaq(2)"
                      class="w-full text-left p-5 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 class="font-semibold text-base text-gray-900 pr-4">
                        Puis-je conserver mon numéro ?
                      </h3>
                      <div
                        class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200"
                        :class="
                          openFaq === 2
                            ? 'bg-emerald-100 rotate-180'
                            : 'bg-gray-100'
                        "
                      >
                        <svg
                          class="w-4 h-4"
                          :class="
                            openFaq === 2 ? 'text-emerald-700' : 'text-gray-600'
                          "
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>
                    <div v-show="openFaq === 2" class="px-5 pb-5 pt-0">
                      <p class="text-gray-600 leading-relaxed">
                        Cette eSIM est une ligne data séparée. Vous conservez
                        votre numéro principal qui reste actif sur votre carte
                        SIM habituelle.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </aside>
      </main>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Countries } from '@features/shared/libraries/countries';

const route = useRoute();
const router = useRouter();
const { proxy } = getCurrentInstance();
const { $stores } = proxy;

// Data
const packageInfo = ref(null);
const country = ref(null);
const loading = ref(true);
const error = ref(null);
const packages = ref([]);

// Form state
const quantity = ref(1);

// FAQ state
const openFaq = ref(null);

const toggleFaq = (index) => {
  openFaq.value = openFaq.value === index ? null : index;
};

// Parse slug from URL (format: packageCode-countryCode)
const parseSlug = () => {
  const slug = route.params.slug;
  if (!slug) return null;

  const parts = slug.split('-');
  if (parts.length < 2) return null;

  const packageCode = parts.slice(0, -1).join('-'); // Rejoin in case packageCode has hyphens
  const countryCode = parts[parts.length - 1].toUpperCase();

  return { packageCode, countryCode };
};

const incrementQuantity = () => {
  quantity.value++;
};

const decrementQuantity = () => {
  if (quantity.value > 1) {
    quantity.value--;
  }
};

// Map country code to esim location code
const getEsimLocationCode = (countryCode) => {
  const mapping = {
    BJ: 'BJ',
    TG: 'TG',
    CI: 'CI',
    SN: 'SN',
    ML: 'ML',
    FR: 'FR',
    BE: 'BE',
    CH: 'CH',
    CM: 'CM',
    CD: 'CD',
    NE: 'NE',
    BF: 'BF',
    GN: 'GN'
  };
  return mapping[countryCode] || countryCode;
};

const formatData = (volume) => {
  if (!volume) return 'Illimité';
  // If already formatted as string (e.g., "1GB", "500MB"), return as-is
  if (typeof volume === 'string') return volume;
  // If numeric, convert from bytes to GB/MB
  const gb = volume / (1024 * 1024 * 1024);
  if (gb >= 1) return `${Math.round(gb)}GB`;
  const mb = volume / (1024 * 1024);
  return `${Math.round(mb)}MB`;
};

const formatPrice = (price) => {
  if (!price) return '---';
  // API returns prices in cents, divide by 100
  const priceInUnits = price / 100;
  // Get currency from selected country, default to XOF
  const currency = country.value?.currency || 'XOF';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0
  }).format(priceInUnits);
};

const calculateTotal = () => {
  if (!packageInfo.value?.price) return 0;
  return packageInfo.value.price * quantity.value;
};

const fetchPackages = async (locationCode) => {
  loading.value = true;
  error.value = null;

  try {
    const { bus } = await import('@core/events/eventSystem');
    const constants = await import('../constants');

    return new Promise((resolve, reject) => {
      const successHandler = (data) => {
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_SUCCESS, successHandler);
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_ERROR, errorHandler);
        packages.value = data || [];
        loading.value = false;
        resolve();
      };

      const errorHandler = (err) => {
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_SUCCESS, successHandler);
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_ERROR, errorHandler);
        packages.value = [];
        loading.value = false;
        reject(err);
      };

      bus.on(constants.EVENTS.ESIM.FETCH_PACKAGES_SUCCESS, successHandler);
      bus.on(constants.EVENTS.ESIM.FETCH_PACKAGES_ERROR, errorHandler);

      bus.emit(constants.EVENTS.ESIM.FETCH_PACKAGES, locationCode);
    });
  } catch (e) {
    packages.value = [];
    loading.value = false;
  }
};

const goToCheckout = () => {
  const parsed = parseSlug();

  router.push({
    path: '/checkout/quick',
    query: {
      orderType: 'esim',
      packageCode: packageInfo.value?.packageCode,
      countryCode: country.value?.countryCode,
      amount: calculateTotal(),
      quantity: quantity.value
    }
  });
};

onMounted(async () => {
  const parsed = parseSlug();

  if (!parsed) {
    error.value = 'URL invalide';
    loading.value = false;
    return;
  }

  const { packageCode, countryCode } = parsed;

  // Find country from library
  const foundCountry = Countries.find(countryCode);
  if (!foundCountry) {
    error.value = 'Pays non trouvé';
    loading.value = false;
    return;
  }

  country.value = foundCountry;

  // Update global store with selected country
  $stores.global.selectCountry(countryCode);

  // Fetch packages from API
  const locationCode = getEsimLocationCode(countryCode);
  await fetchPackages(locationCode);

  // Find the specific package
  const foundPackage = packages.value.find(
    (pkg) => pkg.packageCode === packageCode
  );
  if (!foundPackage) {
    error.value = 'Forfait non trouvé';
    loading.value = false;
    return;
  }

  packageInfo.value = foundPackage;
});
</script>

<style scoped>
/* Custom transitions for FAQ */
.bg-gray-100 > div {
  transition: all 0.3s ease;
}
</style>
